import React, { useState, lazy } from 'react';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const volumes = [{
  images: ['https://cdn.magicpatterns.com/uploads/tq45Thpd45LDyHLHFRyHb4/image.png', 'https://cdn.magicpatterns.com/uploads/xAZSGuTLPmciaBSXGgJTHj/image.png', 'https://cdn.magicpatterns.com/uploads/g3EfcxHfyFE6HpXeHQ2aZA/image.png'],
  title: 'Back In Time, The Cultural Analysis',
  body: '190 pages. Why the film shaped American cultural identity from 1985 to today. Not a summary. A documented analysis.'
}, {
  images: ['https://cdn.magicpatterns.com/uploads/rerwYoQ8gs22KNkXTHs5Gf/image.png', 'https://cdn.magicpatterns.com/uploads/rLQyuhnh2qhC64hG3pEah5/image.png', 'https://cdn.magicpatterns.com/uploads/vxi7ma652Y1wW88rpSxwZe/image.png'],
  title: 'Behind the Clock Tower, The Production Story',
  body: '87 pages. The production stories that never made it into DVD extras. The decisions that almost broke the film.'
}, {
  images: ['https://cdn.magicpatterns.com/uploads/gzWz855tfQDUeagTytQYCe/image.png', 'https://cdn.magicpatterns.com/uploads/3NyPkbzUAwACdE2zMZ8MuJ/image.png', 'https://cdn.magicpatterns.com/uploads/aN6H3njHZtV8jQu2BhuyAk/image.png'],
  title: 'Great Scott! 40 Reflections for 40 Years',
  body: '87 pages. 40 voices across 40 years on why this film changed how they see the world.'
}, {
  images: ['https://cdn.magicpatterns.com/uploads/vRUrFezUsXr4NeCCTTcL6b/image.png', 'https://cdn.magicpatterns.com/uploads/7sMQijfdPvvuxFFNxak5uH/image.png', 'https://cdn.magicpatterns.com/uploads/4LoVuiUpCZ3tw4hi51F4TM/image.png'],
  title: 'Time. Imagination. Destiny. The Philosophical Legacy',
  body: '71 pages. The essay on identity, free will, and why the ending has haunted you for decades.'
}];
// ── Lightbox modal ────────────────────────────────────────────────────────────
const Lightbox = ({
  images,
  startIndex,
  title,
  onClose
}: {
  images: string[];
  startIndex: number;
  title: string;
  onClose: () => void;
}) => {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i => i === 0 ? images.length - 1 : i - 1);
  const next = () => setIdx(i => i === images.length - 1 ? 0 : i + 1);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={onClose} data-id="element-603">
      <div className="relative w-full max-w-sm bg-bttf-bg border border-bttf-primary/30 rounded-xl overflow-hidden shadow-[0_0_40px_rgba(255,108,0,0.25)]" onClick={e => e.stopPropagation()} data-id="element-604">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-bttf-primary/20" data-id="element-605">
          <span className="text-xs font-bold font-future text-bttf-primary uppercase tracking-wide truncate pr-2" data-id="element-606">
            {title}
          </span>
          <button onClick={onClose} className="text-bttf-subtext hover:text-bttf-text transition-colors p-1 rounded-full hover:bg-white/5 flex-shrink-0" aria-label="Cerrar" data-id="element-607">
            <XIcon className="h-4 w-4" data-id="element-608" />
          </button>
        </div>

        {/* Image */}
        <div className="relative bg-black" style={{
        aspectRatio: '3/4'
      }} data-id="element-609">
          <img src={images[idx]} alt={`${title} — página ${idx + 1}`} className="w-full h-full object-contain" decoding="async" data-id="element-610" />
          <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-bttf-primary text-white p-2 rounded-full transition-all" aria-label="Anterior" data-id="element-611">
            <ChevronLeftIcon className="h-4 w-4" data-id="element-612" />
          </button>
          <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-bttf-primary text-white p-2 rounded-full transition-all" aria-label="Siguiente" data-id="element-613">
            <ChevronRightIcon className="h-4 w-4" data-id="element-614" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 py-3" data-id="element-615">
          {images.map((_, i) => <button key={i} onClick={() => setIdx(i)} className={`h-2 rounded-full transition-all duration-300 ${i === idx ? 'bg-bttf-primary w-5' : 'bg-bttf-subtext/30 w-2'}`} aria-label={`Página ${i + 1}`} data-id="element-616" />)}
        </div>

        {/* CTA */}
        <div className="px-4 pb-4" data-id="element-617">
          <a href="#pricing-top" onClick={onClose} className="cta block w-full text-center bg-bttf-primary hover:bg-bttf-primary-hover text-bttf-text font-bold py-3 rounded-md text-sm transition-all font-body uppercase tracking-wide" data-id="element-618">
            Get My Copy →
          </a>
        </div>
      </div>
    </div>;
};
// ── Book block ────────────────────────────────────────────────────────────────
const BookBlock = ({
  images,
  title,
  body
}: {
  images: [string, string, string];
  title: string;
  body: string;
}) => {
  const [lightbox, setLightbox] = useState<number | null>(null);
  return <div className="mb-10 md:mb-14" data-id="element-619">
      {/* 3 images side by side — clickable */}
      <div className="flex gap-2 mb-4" data-id="element-620">
        {images.map((src, i) => <button key={i} type="button" onClick={() => setLightbox(i)} className="flex-1 rounded-lg overflow-hidden shadow-md border border-bttf-subtext/20 hover:border-bttf-primary/50 transition-colors duration-200 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-bttf-primary/50" aria-label={`Ver ${title} — página ${i + 1}`} data-id="element-621">
            <img src={src} alt={`${title}, página ${i + 1}`} className="w-full object-cover" style={{
          aspectRatio: '3/4'
        }} loading="lazy" decoding="async" data-id="element-622" />
          </button>)}
      </div>

      {/* Title */}
      <h3 className="text-lg md:text-xl font-bold font-future text-bttf-primary mb-1.5 leading-snug" data-id="element-623">
        {title}
      </h3>
      {/* Body */}
      <p className="text-sm md:text-base text-bttf-subtext font-body leading-relaxed" data-id="element-624">
        {body}
      </p>

      {lightbox !== null && <Lightbox images={images} startIndex={lightbox} title={title} onClose={() => setLightbox(null)} data-id="element-625" />}
    </div>;
};
export const WhatsInsideSection = () => <section className="py-10 md:py-16 bg-bttf-bg border-t border-bttf-subtext/20 relative overflow-hidden" data-id="element-626">
    <div className="container mx-auto px-4 relative z-10 max-w-2xl" data-id="element-627">
      {/* Header */}
      <div className="text-center mb-10 md:mb-12" data-id="element-628">
        <h2 className="text-2xl md:text-3xl font-bold font-future text-bttf-text mb-1" data-id="element-629">
          What's Waiting Inside:
        </h2>
        <p className="text-xl md:text-2xl font-bold font-future text-bttf-primary" data-id="element-630">
          The Complete 40th Anniversary Archive, 4 Volumes
        </p>
      </div>

      {/* Book blocks */}
      {volumes.map((vol, i) => <BookBlock key={i} images={vol.images as [string, string, string]} title={vol.title} body={vol.body} data-id="element-631" />)}

      <p className="text-center text-bttf-subtext/50 italic text-sm mt-2 font-body" data-id="element-632">
        Delivered instantly. Readable on any device.
      </p>
    </div>
  </section>;